import { Send, Instagram } from "lucide-react";
import type { SVGProps } from "react";
import type { LegalView } from "../../pages/legal/legalPages";

type FooterLink = {
  label: string;
  /** External / anchor link. */
  href?: string;
  /** In-app legal page — rendered as a button that calls `onShowLegal`. */
  view?: LegalView;
};
type FooterCol = {
  title: string;
  /** Makes the column heading itself navigate to a legal page. */
  titleView?: LegalView;
  links: FooterLink[];
};

const COLS: FooterCol[] = [
  {
    title: "Product",
    links: [
      { label: "Games",        href: "#games-grid"  },
      { label: "Tournaments",  href: "#tournaments" },
      { label: "Leaderboard",  href: "#leaderboard" },
      { label: "Stats",        href: "#stats"       },
    ],
  },
  {
    title: "Developers",
    links: [
      { label: "Documentation", href: "#docs"      },
      { label: "Smart Contract", href: "https://solscan.io/account/RAW7dgEuw7NUs8fXd1LgvNddGr1d85Yv8959ATVUwYw" },
      { label: "GitHub",         href: "https://github.com/leoarx/Hupper" },
      { label: "Audit Report",   href: "#audit"     },
    ],
  },
  {
    title: "Support",
    links: [
      { label: "Help Center",  href: "#help"             },
      { label: "FAQ",          href: "#faq"              },
      { label: "Status",       href: "#status"           },
      { label: "Contact",      href: "mailto:support@hupper.com" },
    ],
  },
  {
    title: "Legal",
    titleView: "legal",
    links: [
      { label: "Terms of Service",   view: "terms"              },
      { label: "Privacy Policy",     view: "privacy"            },
      { label: "Responsible Gaming", view: "responsible-gaming" },
      { label: "Restricted Regions", view: "restricted-regions" },
    ],
  },
];

/** Official X (formerly Twitter) brand logo — lucide ships no brand icon for it. */
const XLogo = (props: SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z" />
  </svg>
);

/** Official Discord brand logo — lucide ships no brand icon for it. */
const DiscordLogo = (props: SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z" />
  </svg>
);

/** Official TikTok brand logo — lucide ships no brand icon for it. */
const TikTokLogo = (props: SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
  </svg>
);

/** Official YouTube brand logo — lucide ships no brand icon for it. */
const YouTubeLogo = (props: SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
  </svg>
);

const SOCIALS = [
  { Icon: XLogo,          label: "X",         href: "https://x.com/hupperfun" },
  { Icon: DiscordLogo,    label: "Discord",   href: "#" },
  { Icon: Send,           label: "Telegram",  href: "#" },
  { Icon: TikTokLogo,     label: "TikTok",    href: "#" },
  { Icon: Instagram,      label: "Instagram", href: "#" },
  { Icon: YouTubeLogo,    label: "YouTube",   href: "#" },
];

/** Links that open an in-app panel instead of navigating. Matched by label. */
const ACTION_LABELS = new Set(["Status", "Documentation"]);

const Logo = () => (
  <svg viewBox="0 0 60 60" fill="none" className="w-6 h-6" aria-hidden="true">
    <path d="M6 6 L26 18 L26 42 L6 54 Z" stroke="#FF6B00" strokeWidth="5" strokeLinejoin="miter" />
    <path d="M54 6 L34 18 L34 42 L54 54 Z" stroke="#FF6B00" strokeWidth="5" strokeLinejoin="miter" />
  </svg>
);

interface FooterProps {
  /** Opens the system-status window (the "Status" link). */
  onShowStatus?: () => void;
  /** Opens the in-app docs page (the "Documentation" link). */
  onShowDocs?: () => void;
  /** Navigates to a legal page (the "Legal" column links + heading). */
  onShowLegal?: (view: LegalView) => void;
}

export default function Footer({ onShowStatus, onShowDocs, onShowLegal }: FooterProps = {}) {
  return (
    <footer className="hairline-t bg-[var(--color-canvas)]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Top: brand + cols */}
        <div className="grid grid-cols-2 md:grid-cols-12 gap-10 md:gap-8 py-14 md:py-20">
          {/* Brand block */}
          <div className="col-span-2 md:col-span-4 flex flex-col gap-5">
            <div className="flex items-center gap-2">
              <Logo />
              <span className="text-[18px] font-semibold tracking-[-0.02em] lowercase text-[var(--color-text-primary)]">
                hupper
              </span>
            </div>
            <p className="text-[13px] text-[var(--color-text-secondary)] leading-relaxed max-w-xs">
              Skill-based wagering settled on Solana. Non-custodial, audited,
              open-source. Win by skill — paid in USDT.
            </p>
            {/* Socials */}
            <div className="flex items-center gap-1.5 mt-1">
              {SOCIALS.map(({ Icon, label, href }) => (
                <a
                  key={label}
                  href={href}
                  aria-label={label}
                  target={href.startsWith("http") ? "_blank" : undefined}
                  rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
                  className="w-9 h-9 rounded-md hairline-t hairline-b hairline-l hairline-r flex items-center justify-center text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)] hover:border-[var(--color-line-emphasis)] transition-colors duration-150"
                >
                  <Icon className="w-3.5 h-3.5" strokeWidth={1.75} />
                </a>
              ))}
            </div>
          </div>

          {/* Link columns */}
          {COLS.map((col) => {
            const titleView = col.titleView;
            return (
              <div key={col.title} className="col-span-1 md:col-span-2 flex flex-col gap-4">
                {titleView ? (
                  <button
                    type="button"
                    onClick={() => onShowLegal?.(titleView)}
                    className="text-left text-[10px] uppercase tracking-[0.16em] text-[var(--color-text-tertiary)] hover:text-[var(--color-text-secondary)] font-medium bg-transparent border-0 p-0 cursor-pointer transition-colors duration-150"
                  >
                    {col.title}
                  </button>
                ) : (
                  <div className="text-[10px] uppercase tracking-[0.16em] text-[var(--color-text-tertiary)] font-medium">
                    {col.title}
                  </div>
                )}
                <ul className="flex flex-col gap-2.5">
                  {col.links.map((link) => {
                    const linkClass =
                      "text-[13px] text-left text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] transition-colors duration-150 bg-transparent border-0 p-0 cursor-pointer font-[inherit]";

                    // In-app legal page → button that drives navigation.
                    const view = link.view;
                    if (view) {
                      return (
                        <li key={link.label}>
                          <button type="button" onClick={() => onShowLegal?.(view)} className={linkClass}>
                            {link.label}
                          </button>
                        </li>
                      );
                    }

                    // In-app action (Status / Documentation panels).
                    if (ACTION_LABELS.has(link.label)) {
                      return (
                        <li key={link.label}>
                          <button
                            type="button"
                            onClick={() => {
                              if (link.label === "Status") onShowStatus?.();
                              if (link.label === "Documentation") onShowDocs?.();
                            }}
                            className={linkClass}
                          >
                            {link.label}
                          </button>
                        </li>
                      );
                    }

                    // Regular external / anchor link.
                    return (
                      <li key={link.label}>
                        <a
                          href={link.href}
                          className="text-[13px] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] transition-colors duration-150"
                        >
                          {link.label}
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Bottom: meta */}
        <div className="hairline-t py-6 flex flex-col md:flex-row items-start md:items-center justify-start gap-4 text-[12px] font-mono tabular-nums text-[var(--color-text-tertiary)]">
          <div className="flex items-center gap-5">
            <span>© {new Date().getFullYear()} Hupper Labs</span>
            <span className="hidden md:inline text-[var(--color-text-disabled)]">·</span>
            <span className="hidden md:inline">Built for crypto-native gamers</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
